/* eslint-disable check-file/filename-blocklist -- pre-existing .jsx; this change only swaps an icon import, converting to .tsx is a separate migration */
import { Text, Box, HStack, Progress, Flex, Icon } from '@chakra-ui/react';
import { CheckCircle } from '@kula-ai/icons-core';

const SCORE_COLOR_MAPPING = ['red', 'red', 'red', 'orange', 'orange', 'green'];
const SCORE_TEXT_MAPPING = [
  '', // 0 - empty state
  'Very Weak Password', // 1
  'Very Weak Password', // 2
  'Weak Password', // 3
  'Weak Password', // 4
  'Strong Password', // 5
];

// eslint-disable-next-line no-useless-escape
const SPECIALCHARREGEX = /[~`¿¡!#$%\^&*€£@+÷=\-\[\]\\';,/{}\(\)|\\":<>\?\.\_]/;

// Renders the password strength meter based on the given `score` (1 to 5).
// If `score` is 0 or not provided, the meter will be empty.
export function PasswordStrengthMeter({ score }) {
  if (score > 5) {
    score = 5; // clip if above 5
  } else if (!(score > 0)) {
    score = 0; // default to empty if not in (1 to 5) range
  }
  const colorScheme = SCORE_COLOR_MAPPING[score];
  const text = SCORE_TEXT_MAPPING[score];

  return (
    <Box>
      <HStack spacing={1} py={2}>
        <Progress
          value={score < 1 ? 0 : 100}
          size="sm"
          width="20%"
          colorScheme={colorScheme}
          borderRadius="4px 0 0 4px"
        />
        <Progress
          value={score < 2 ? 0 : 100}
          size="sm"
          width="20%"
          colorScheme={colorScheme}
        />
        <Progress
          value={score < 3 ? 0 : 100}
          size="sm"
          width="20%"
          colorScheme={colorScheme}
        />
        <Progress
          value={score < 4 ? 0 : 100}
          size="sm"
          width="20%"
          colorScheme={colorScheme}
        />
        <Progress
          value={score < 5 ? 0 : 100}
          size="sm"
          width="20%"
          colorScheme={colorScheme}
          borderRadius="0 4px 4px 0"
        />
      </HStack>
      {text && (
        <Box>
          <Text fontWeight="bold" color={colorScheme} as="span" p="0">
            {text}.
          </Text>
          {text !== SCORE_TEXT_MAPPING[5] && (
            <Text color={colorScheme} as="span" pl="4px">
              Ensure it has 12 characters or more, and contains 1 number, 1
              capital letter, and 1 special character.
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
}

const CheckListItem = ({ satisfied = false, text = '' }) => {
  return (
    <Flex gap="12px">
      <Icon
        as={CheckCircle}
        color={satisfied ? 'success-200' : 'error-200'}
        boxSize="20px"
      />
      <Text color={satisfied ? 'success-200' : 'error-200'}>{text}</Text>
    </Flex>
  );
};

export const PasswordHelper = ({
  right = '0px',
  top = '0px',
  mt = '0px',
  value = '',
}) => {
  const checkLists = [
    {
      id: 'length',
      text: 'At least 12 characters',
      condition: value?.length >= 12,
    },
    {
      id: 'special',
      text: 'At least 1 special character',
      condition: SPECIALCHARREGEX?.test(value),
    },
    {
      id: 'number',
      text: 'At least 1 number',
      condition: /\d/g?.test(value),
    },
    {
      id: 'capital',
      text: 'At least 1 uppercase letter',
      condition: /[A-Z]/g?.test(value),
    },
  ];
  return (
    <Flex
      pos="absolute"
      right={right}
      top={top}
      mt={mt}
      border="1px var(--kula-dark-secondary-200, #E0E0E0) solid"
      boxShadow="0px 4px 12px rgba(22, 22, 22, 0.10)"
      p="20px"
      bg="var(--kula-colors-white, #fff)"
      borderRadius="6px"
      maxW="250px"
      flexDir="column"
      gap="12px"
      zIndex="5"
    >
      <Text fontWeight="semiBold" fontSize="14px">
        Your password must contain:
      </Text>
      {checkLists?.map((item) => (
        <CheckListItem
          key={item?.id}
          satisfied={item?.condition}
          text={item?.text}
        />
      ))}
    </Flex>
  );
};
